import type { CoApplicantInviteForm } from '~/use-cases/types/CoApplicantInviteForm';
import type { LandlordVerificationForm, LandlordVerificationState } from '~/use-cases/types/LandlordVerification';
import { RentalApplicationErrorScope } from '~/use-cases/types/RentalApplicationErrorScope';
import type { RentalApplicationErrors } from '~/use-cases/types/RentalApplicationErrors';
import type { RentalApplicationInfo } from '~/use-cases/types/RentalApplicationInfo';
import { RentalApplicationSteps } from '~/use-cases/types/RentalApplicationSteps';
import type { SignablePdf } from '~/use-cases/types/SignablePdf';
import type { MainAppState } from './appStateTypes';

export type * from './appStateTypes';

export const emptySignablePdf = (): SignablePdf => ({
  pageSizes: [],
  pageAnnotations: new Map(),
});

export const emptyCoApplicantInviteForm = (): CoApplicantInviteForm => ({
  values: {},
  errors: new Set(),
});

export const emptyRentalApplicationErrors = (): RentalApplicationErrors => ({
  [RentalApplicationSteps.GET_STARTED]: {},
  [RentalApplicationSteps.APPLICANT_INFORMATION]: {},
  [RentalApplicationSteps.RENTAL_HISTORY]: [],
  [RentalApplicationSteps.WORK_HISTORY]: [],
  [RentalApplicationSteps.PETS_AND_CARS]: [],
  [RentalApplicationSteps.QUESTIONS]: {},
  [RentalApplicationSteps.TERMS_AND_CONDITIONS]: {},
  [RentalApplicationSteps.TRANS_UNION_QUESTIONS]: {},
  [RentalApplicationErrorScope.SingleField]: {},
});

export const emptyRentalApplicationInfo = (): RentalApplicationInfo => ({
  application: {
    credentials: {},
    newFiles: [],
  },
  transUnionAnswers: {},
  isPaid: false,
  errors: emptyRentalApplicationErrors(),
  fileSlots: [],
  stepChecks: {},
  coApplicantInvite: emptyCoApplicantInviteForm(),
  currentStep: RentalApplicationSteps.APPLICANT_INFORMATION,
});

export const emptyLandlordVerificationForm = (): LandlordVerificationForm => ({
  hadLatePayments: false,
  wasEvicted: false,
  wouldRecommend: false,
  wantsMagicDoorInfo: true,
});

export const emptyLandlordVerificationState = (): LandlordVerificationState => ({
  notFound: false,
  form: emptyLandlordVerificationForm(),
});

export const createMainAppState = (): MainAppState => ({
  user: {
    unitsListing: {
      items: [],
      preSelectUnits: [],
      currentPage: 0,
      pageSize: 10,
      totalCount: 0,
      totalPages: 0,
    },
    rentalApplication: emptyRentalApplicationInfo(),
    signerState: {
      document: emptySignablePdf(),
      signedAnnotations: new Set<string>(),
      progress: { signedRequired: 0, totalRequired: 1 },
      hasAcceptedESignatureConsent: false,
      consentForm: { agreed: false, errors: new Set() },
      signingOffers: {
        visible: false,
        dwDismissed: false,
        rbpSkipped: false,
        joinedPackageIds: [],
        impressionTracked: false,
        isEligibilityRestored: false,
      },
    },
    rentersInsurance: {
      isPurchased: false,
    },
    landlordVerification: emptyLandlordVerificationState(),
    likedUnitIds: [],
  },
});
